import { useState, useEffect, useRef } from 'react';

interface HtmlPreviewProps {
  taskId: string;
  progress: number;
}

export function HtmlPreview({ taskId, progress }: HtmlPreviewProps) {
  const [showPreview, setShowPreview] = useState(false);
  const [htmlUrl, setHtmlUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // 当进度达到50%时，HTML应该已经生成
  useEffect(() => {
    if (progress >= 50 && !htmlUrl) {
      setHtmlUrl(`/api/tasks/${taskId}/preview`);
    }
  }, [progress, taskId, htmlUrl]);

  // 只有在生成中或完成后才显示预览按钮
  if (progress < 50) {
    return null;
  }

  if (showPreview) {
    return (
      <div className="mt-6 border border-gray-300 rounded-lg overflow-hidden bg-white">
        <div className="bg-gray-100 px-4 py-2 flex items-center justify-between border-b border-gray-300">
          <h3 className="font-medium text-gray-700">HTML 预览</h3>
          <button
            onClick={() => setShowPreview(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            关闭
          </button>
        </div>
        <div className="w-full overflow-x-auto overflow-y-auto bg-white relative" style={{ maxHeight: '70vh' }}>
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/90 z-10">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                <p className="text-sm text-gray-600">加载预览中...</p>
              </div>
            </div>
          )}
          <iframe
            ref={iframeRef}
            src={htmlUrl}
            className="w-full min-w-[1280px] border-0"
            style={{ minHeight: '600px' }}
            title="HTML Preview"
            sandbox="allow-same-origin allow-scripts"
            onLoad={() => {
              setIsLoading(false);
              const doc = iframeRef.current?.contentDocument;
              if (!doc) return;

              const styleId = 'aippt-preview-scroll-override';
              if (doc.getElementById(styleId)) return;

              const style = doc.createElement('style');
              style.id = styleId;
              style.textContent = `
                html, body {
                  overflow: auto !important;
                  height: auto !important;
                  min-height: 100% !important;
                }
                body {
                  padding-bottom: 24px !important;
                }
                .slide-container {
                  height: auto !important;
                  min-height: 720px !important;
                  margin-bottom: 20px !important;
                }
              `;
              doc.head.appendChild(style);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 flex gap-4">
      <button
        onClick={() => {
          setShowPreview(true);
          setIsLoading(true);
        }}
        className="flex-1 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={isLoading}
      >
        {isLoading ? '加载中...' : '查看 HTML 预览'}
      </button>
    </div>
  );
}
